/**
 * Cloud-storage credential form.
 *
 * Shared by the settings dialog and the onboarding wizard so both walk the
 * same validate/test/save path against the same API. The secret key is
 * write-only: the server never sends it back, so the field starts empty and
 * a blank value means "keep the stored key".
 */
import { useRef, useState, type FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import {
  settingsApi,
  type StorageProviderId,
  type StorageSettingsInput,
  type StorageSettingsView,
} from '../lib/api/settings.js';
import { ApiError } from '../lib/api/client.js';

type Status = { kind: 'idle' } | { kind: 'ok' | 'error'; message: string };

const PROVIDERS: Array<{ id: StorageProviderId; label: string }> = [
  { id: 'tencent_cos', label: 'Tencent COS' },
  { id: 'amazon_s3', label: 'Amazon S3' },
];

interface StorageCredentialFormProps {
  initial: StorageSettingsView | null;
  /** Primary action label: "Save" in the dialog, "Save and continue" onboarding. */
  saveLabel?: string;
  /** Rendered between Test and the primary action (the dialog's Cancel). */
  extraActions?: React.ReactNode;
  /** Reports test/save busy state so a modal can block Escape/backdrop close. */
  onBusyChange?: (busy: boolean) => void;
  onSaved: (storage: StorageSettingsView) => void;
}

export function StorageCredentialForm({
  initial,
  saveLabel = 'Save',
  extraActions,
  onBusyChange,
  onSaved,
}: StorageCredentialFormProps) {
  const [provider, setProvider] = useState<StorageProviderId>(initial?.provider ?? 'tencent_cos');
  const [region, setRegion] = useState(initial?.region ?? '');
  const [bucket, setBucket] = useState(initial?.bucket ?? '');
  const [accessKey, setAccessKey] = useState(initial?.accessKey ?? '');
  const [secretKey, setSecretKey] = useState('');
  const [endpoint, setEndpoint] = useState(initial?.endpoint ?? '');

  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const locked = initial?.lockedByEnv === true;
  const busy = testing || saving;

  function currentInput(): StorageSettingsInput {
    return {
      provider,
      region: region.trim(),
      bucket: bucket.trim(),
      accessKey: accessKey.trim(),
      // Blank means "unchanged" — the server keeps the stored key.
      secretKey: secretKey.trim(),
      endpoint: endpoint.trim(),
    };
  }

  async function onTest(): Promise<void> {
    setTesting(true);
    onBusyChange?.(true);
    setStatus({ kind: 'idle' });
    try {
      const result = await settingsApi.testStorage(currentInput());
      setStatus({
        kind: result.ok ? 'ok' : 'error',
        message: result.ok ? `Connected to ${result.host ?? 'the bucket'}.` : result.message,
      });
    } catch (caught) {
      setStatus({
        kind: 'error',
        message: caught instanceof ApiError ? caught.message : 'Could not reach the bucket.',
      });
    } finally {
      setTesting(false);
      onBusyChange?.(false);
    }
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (busy) return;
    setSaving(true);
    onBusyChange?.(true);
    setStatus({ kind: 'idle' });
    try {
      const result = await settingsApi.saveStorage(currentInput());
      setSecretKey('');
      onSaved(result.storage);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        setStatus({
          kind: 'error',
          message: 'Credentials are set by environment variables and cannot be changed here.',
        });
      } else {
        setStatus({
          kind: 'error',
          message: caught instanceof ApiError ? caught.message : 'Could not save the settings.',
        });
      }
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="storage-form">
      {locked && (
        <div className="settings-note">
          Credentials are supplied by environment variables, so they cannot be changed from
          here. The values below are read-only.
        </div>
      )}

      {!initial?.persist && (
        <div className="settings-note">
          The server is running with <code>JNOTE_PERSIST_STORAGE=false</code>, so these
          credentials are kept in memory only and are lost on restart.
        </div>
      )}

      <label className="settings-field">
        <span className="settings-label">Provider</span>
        <select
          className="input"
          value={provider}
          disabled={busy || locked}
          aria-label="Provider"
          onChange={(event) => setProvider(event.target.value as StorageProviderId)}
        >
          {PROVIDERS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      <div className="settings-grid">
        <label className="settings-field">
          <span className="settings-label">Region</span>
          <input
            ref={firstFieldRef}
            className="input"
            value={region}
            placeholder="ap-guangzhou"
            autoComplete="off"
            disabled={busy || locked}
            onChange={(event) => setRegion(event.target.value)}
          />
        </label>

        <label className="settings-field">
          <span className="settings-label">Bucket</span>
          <input
            className="input"
            value={bucket}
            placeholder="my-bucket-1250000000"
            autoComplete="off"
            disabled={busy || locked}
            onChange={(event) => setBucket(event.target.value)}
          />
        </label>

        <label className="settings-field">
          <span className="settings-label">Access key</span>
          <input
            className="input"
            value={accessKey}
            autoComplete="off"
            spellCheck={false}
            disabled={busy || locked}
            onChange={(event) => setAccessKey(event.target.value)}
          />
        </label>

        <label className="settings-field">
          <span className="settings-label">
            Secret key{initial?.hasSecretKey ? ' (stored)' : ''}
          </span>
          <input
            className="input"
            type="password"
            value={secretKey}
            autoComplete="new-password"
            spellCheck={false}
            placeholder={initial?.hasSecretKey ? 'Leave blank to keep the stored key' : ''}
            disabled={busy || locked}
            onChange={(event) => setSecretKey(event.target.value)}
          />
        </label>
      </div>

      <label className="settings-field">
        <span className="settings-label">Endpoint (optional)</span>
        <input
          className="input"
          value={endpoint}
          placeholder="Only for S3-compatible services, e.g. http://127.0.0.1:9000"
          autoComplete="off"
          spellCheck={false}
          disabled={busy || locked}
          onChange={(event) => setEndpoint(event.target.value)}
        />
      </label>

      {status.kind !== 'idle' && (
        <div
          className={
            status.kind === 'ok'
              ? 'settings-status settings-status-ok'
              : 'settings-status settings-status-error'
          }
          role="status"
        >
          {status.message}
        </div>
      )}

      <div className="modal-actions">
        <button type="button" className="button button-quiet" onClick={onTest} disabled={busy}>
          {testing ? (
            <>
              <Loader2 size={13} strokeWidth={2} className="spin" /> Testing…
            </>
          ) : (
            'Test connection'
          )}
        </button>
        {extraActions}
        <button type="submit" className="button" disabled={busy || locked}>
          {saving ? 'Saving…' : saveLabel}
        </button>
      </div>
    </form>
  );
}
